/**
 * Central Google Maps (Places) loader.
 *
 * Address components MUST NOT ship their own <script src="maps.googleapis...">
 * tags: they sit in Livewire-morphed DOM, and morphdom inserts script nodes
 * without executing them — so a component that first appears mid-flow got a
 * Google that never loads. This module owns the script exactly once, retries
 * transient failures, and hands components a promise they can await whenever
 * they initialize (full load, Turbo visit, or Livewire morph alike).
 *
 * Components treat Google as a progressive enhancement: they bind manual entry
 * unconditionally and only upgrade to autocomplete when this resolves.
 */

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1500;

let pending = null;

function apiKey() {
    const meta = document.querySelector('meta[name="google-maps-key"]');
    return meta ? meta.content : '';
}

function ready() {
    return typeof window.google !== 'undefined'
        && window.google.maps
        && window.google.maps.places;
}

function injectScript(key) {
    return new Promise((resolve, reject) => {
        const src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) + '&libraries=places';
        const existing = document.querySelector('script[src^="https://maps.googleapis.com/maps/api/js"]');
        if (existing) {
            // A tag exists (stray morph leftovers included). Poll briefly for it
            // to finish instead of double-loading the API.
            let waited = 0;
            const poll = setInterval(() => {
                if (ready()) { clearInterval(poll); resolve(); }
                else if ((waited += 200) >= 10000) { clearInterval(poll); existing.remove(); reject(new Error('gmaps stalled')); }
            }, 200);
            return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => (ready() ? resolve() : reject(new Error('gmaps loaded without places')));
        script.onerror = () => { script.remove(); reject(new Error('gmaps script error')); };
        document.head.appendChild(script);
    });
}

export default function loadGoogleMaps() {
    if (ready()) return Promise.resolve(window.google);
    if (pending) return pending;

    const key = apiKey();
    if (!key) return Promise.reject(new Error('gmaps key missing'));

    pending = (async () => {
        for (let attempt = 0; ; attempt++) {
            try {
                await injectScript(key);
                return window.google;
            } catch (e) {
                if (attempt >= MAX_RETRIES) {
                    pending = null; // allow a later caller to try again
                    throw e;
                }
                await new Promise(r => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
            }
        }
    })();

    return pending;
}
